/**
 * Single source of truth for every public data claim on the marketing site.
 *
 * RULES (do not break these):
 *  1. Never publish a number larger than the verified actual count. Always round DOWN.
 *  2. If the underlying database count changes, update this file first, then sweep
 *     the prose that repeats it. Never invent a new figure in a template.
 *  3. `actual` values are internal reference only. Never render them.
 *  4. Update cadence copy must match reality. Successors and probate are refreshed
 *     weekly because county courts publish weekly, not daily.
 */

export interface DataFact {
  /** The string that is safe to publish. Always rounded down from `actual`. */
  readonly label: string;
  /** Verified record count at the last audit. Internal reference only. */
  readonly actual: number;
  /** Human readable refresh cadence, safe to publish. */
  readonly cadence: string;
}

export const DataFacts = {
  Assessors: {
    label: "160M+",
    actual: 169_105_010,
    cadence: "updated daily"
  },
  PreForeclosure: {
    label: "1.4M+",
    actual: 1_458_376,
    cadence: "updated daily"
  },
  Successors: {
    label: "4M+",
    actual: 4_625_885,
    cadence: "updated weekly, every Tuesday"
  },
  Probate: {
    label: "600,000+",
    actual: 673_528,
    cadence: "updated weekly, every Tuesday"
  }
} as const satisfies Record<string, DataFact>;

/** Counties with usable coverage. ~3,140 verified; published rounded down. */
export const CountiesLabel = "3,000+";

/** Every state plus DC. */
export const StatesLabel = "all 50 states";

/**
 * Why successor and probate data is weekly rather than daily. Reuse this
 * sentence verbatim so the explanation stays consistent site wide.
 */
export const WeeklyCadenceReason =
  "County probate courts publish their filings on a weekly schedule, so we refresh successor and probate records every Tuesday rather than claiming a daily feed we cannot deliver.";
